import { Component, computed, inject, output, signal, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { WorkMontageplanNoteVisibilityRoleDto, WorkMontageplanV2Service } from '@work-frontend-monorepo/work-rest-api';
import { ModalComponent } from '../modal/modal.component';
import { ButtonComponent } from '../button/button.component';
import { InputFieldComponent } from '../inputs/input-field/input-field.component';
import { BadgeComponent } from '../badge/badge.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { ExceptionHandlingService } from '../../services/exception-handling/exception-handling.service';
import { LoadingAndFeedbackState } from '../../models/loading-and-feedback-state.type';

export interface MontageplanNoteTarget {
  userId: number;
  name: string;
  date: string;
  note: string | null | undefined;
}

export interface MontageplanNoteSavedEvent {
  userId: number;
  date: string;
  note: string | null;
}

/**
 * Notiz zu einer Person an einem Tag im Montageplan (liegt auf dem Timerecord). Sichtbar und bearbeitbar nur mit
 * `montageplan:notes` — die Berechtigung prüft die einbindende Komponente, bevor sie das Modal öffnet.
 */
@Component({
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    ModalComponent,
    ButtonComponent,
    InputFieldComponent,
    BadgeComponent,
    TooltipDirective,
    FormatDatePipe,
  ],
  selector: 'work-ui-montageplan-note-modal',
  templateUrl: './montageplan-note-modal.component.html',
})
export class MontageplanNoteModalComponent {
  private readonly workMontageplanV2Service = inject(WorkMontageplanV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);

  public readonly noteSaved = output<MontageplanNoteSavedEvent>();

  protected readonly target = signal<MontageplanNoteTarget | undefined>(undefined);
  protected readonly saveLoadingState = signal<LoadingAndFeedbackState>('none');
  protected readonly noteFormControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(2000)] });

  private readonly noteModal = viewChild.required<ModalComponent>('noteModal');

  // Wer die Notiz sieht: Rollen mit montageplan:notes / montageplan:admin / all:admin. Einmal pro Komponente geladen.
  private readonly visibilityRoles = signal<WorkMontageplanNoteVisibilityRoleDto[] | undefined>(undefined);
  protected readonly visibilityRoleEntries = computed(() =>
    (this.visibilityRoles() ?? []).map((role) => {
      const userNames = role.users.map((user) => `${user.firstName} ${user.lastName}`.trim());
      return {
        id: role.id,
        label: role.label || role.name,
        userCount: userNames.length,
        // Tooltip für Desktop; auf dem Handy (keine Tooltips) klappt ein Tipp die gleiche Liste auf.
        userNamesText: userNames.length > 0 ? userNames.join(', ') : '',
      };
    }),
  );
  protected readonly visibilityLoaded = computed(() => this.visibilityRoles() !== undefined);
  protected readonly expandedRoleId = signal<number | undefined>(undefined);

  public open(target: MontageplanNoteTarget): void {
    this.target.set(target);
    this.noteFormControl.reset(target.note ?? '');
    this.saveLoadingState.set('none');
    this.expandedRoleId.set(undefined);
    this.noteModal().open();
    void this.loadVisibilityRoles();
  }

  protected toggleRole(roleId: number): void {
    this.expandedRoleId.update((current) => (current === roleId ? undefined : roleId));
  }

  private async loadVisibilityRoles(): Promise<void> {
    if (this.visibilityRoles()) {
      return;
    }
    try {
      this.visibilityRoles.set(await lastValueFrom(this.workMontageplanV2Service.montageplanV2ControllerGetMontageplanNoteVisibility()));
    } catch (loadNoteVisibilityException) {
      // Nur ein Hinweis — ohne ihn lässt sich die Notiz trotzdem speichern.
      this.visibilityRoles.set([]);
    }
  }

  protected async save(): Promise<void> {
    await this.persist(this.noteFormControl.value);
  }

  protected async remove(): Promise<void> {
    await this.persist('');
  }

  private async persist(rawNote: string): Promise<void> {
    const target = this.target();
    if (!target || this.noteFormControl.invalid) {
      return;
    }
    const note = rawNote.trim() ? rawNote.trim() : null;
    this.saveLoadingState.set('loading');
    try {
      await lastValueFrom(
        this.workMontageplanV2Service.montageplanV2ControllerUpdateMontageplanNote({
          userId: target.userId,
          workdate: dayjs(target.date).format('YYYY-MM-DD'),
          body: { montageplanNote: note },
        }),
      );
      this.saveLoadingState.set('success');
      this.noteSaved.emit({ userId: target.userId, date: target.date, note });
      await this.noteModal().close();
      this.target.set(undefined);
    } catch (updateMontageplanNoteException) {
      this.saveLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('updateMontageplanNoteException', updateMontageplanNoteException);
    }
  }
}
